import { useEffect, useState } from 'react';
import { Alert, Button, Checkbox, Input, Modal, Select, Space, Tag, Typography } from 'antd';
import { apiGet, apiPost } from '../api/client';
import { FilteredTable } from '../list-view/list-view';
import { adminConfig, projectAdmin } from '../list-view/admin-config';
import { beijingDay } from '../list-view/query';

type OnlineSheetTarget = 'ROSTER' | 'EMPLOYEE_LIFECYCLE';
type FieldStatus = 'MAPPED' | 'DERIVED' | 'FORMULA_FILL_DOWN' | 'INTENTIONALLY_BLANK' | 'PENDING_CONFIRMATION';

type FieldPreview = {
  targetHeader: string;
  sourceLabel: string;
  sourcePath: string;
  transform: string;
  previewValue: string;
  status: FieldStatus;
  sensitive: boolean;
};

type MappingPreview = {
  target: OnlineSheetTarget;
  title: string;
  documentName: string;
  documentUrl: string;
  sheetStrategy: string;
  targetSheet: string;
  rowKey: string;
  materialSubmittedAt: string;
  mode: 'REAL_ARCHIVE_PREVIEW';
  employeeName: string;
  externalCallCount: number;
  generatedAt: string;
  previewVersion: string;
  fields: FieldPreview[];
  writePlan: {
    sequence: ['COPY_FORMULAS', 'WRITE_VALUES'];
    formulaSource: '上一数据行' | '最近公式完整的在职行';
    formulaPasteType: 'formulas';
    formulaHeaders: string[];
    valueHeaders: string[];
  };
  syncReadiness: {
    ready: boolean;
    blockerCount: number;
    estimatedExternalCallCount: number;
  };
  syncResult?: SyncResult;
};

type SyncResult = {
  id: string;
  target: OnlineSheetTarget;
  rowKey: string;
  status: 'SUCCEEDED';
  mode: 'DINGTALK_LIVE';
  operator: string;
  completedAt: string;
  externalCallCount: number;
  targetRow?: number;
  verified?: boolean;
};

export function OnlineSheetMappingPage({ target }: { target: OnlineSheetTarget }) {
  return <OnlineSheetMappingContent key={target} target={target} />;
}

function OnlineSheetMappingContent({ target }: { target: OnlineSheetTarget }) {
  const [items, setItems] = useState<MappingPreview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reviewed, setReviewed] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState('');
  const [selectedRowKey, setSelectedRowKey] = useState('');
  const [submittedFrom, setSubmittedFrom] = useState('');
  const [submittedTo, setSubmittedTo] = useState('');
  const changeSubmissionDate = (from: string, to: string) => {
    setSubmittedFrom(from);
    setSubmittedTo(to);
    setSelectedRowKey('');
    setReviewed(false);
    setConfirmOpen(false);
    setSyncError('');
  };

  const load = async () => {
    setLoading(true);
    setError('');
    setReviewed(false);
    setSyncError('');
    try {
      const nextItems = await apiGet<MappingPreview[]>('/admin/online-sheet-mappings/preview');
      setItems(nextItems);
      const currentStillExists = nextItems.some((item) => item.target === target && item.rowKey === selectedRowKey);
      if (!currentStillExists) setSelectedRowKey(nextItems.find((item) => item.target === target)?.rowKey ?? '');
    } catch (nextError) {
      setError((nextError as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => void load(), []);

  const dateError = submittedFrom && submittedTo && submittedFrom > submittedTo;
  const targetItems = items.filter((item) => {
    if (item.target !== target || dateError) return false;
    if (!submittedFrom && !submittedTo) return true;
    const day = beijingDay(item.materialSubmittedAt);
    return Boolean(day && (!submittedFrom || day >= submittedFrom) && (!submittedTo || day <= submittedTo));
  });
  const preview = targetItems.find((item) => item.rowKey === selectedRowKey) ?? targetItems[0];
  const fields = preview?.fields ?? [];
  const totals = summarize(preview?.fields ?? []);
  const fallbackTitle = target === 'ROSTER' ? '花名册表映射' : '入转调离表映射';
  const targetLabel = target === 'ROSTER' ? '花名册' : '入转调离表';
  const employeeName = preview ? fieldValue(preview, '员工姓名') : '';

  const confirmSync = async () => {
    if (!preview) return;
    setSyncing(true);
    setSyncError('');
    try {
      const result = await apiPost<SyncResult>(`/admin/online-sheet-mappings/${target}/sync`, {
        confirmed: true,
        previewVersion: preview.previewVersion,
        rowKey: preview.rowKey
      });
      setItems((current) =>
        current.map((item) =>
          item.target === target && item.rowKey === preview.rowKey ? { ...item, syncResult: result } : item
        )
      );
      setReviewed(false);
      setConfirmOpen(false);
    } catch (nextError) {
      setSyncError(`${employeeName} · ${targetLabel}：${(nextError as Error).message}`);
      setReviewed(false);
      setConfirmOpen(false);
    } finally {
      setSyncing(false);
    }
  };

  return (
    <section className="sheet-mapping-page">
      <div className="sheet-mapping-heading">
        <div>
          <Space align="center" wrap>
            <Typography.Title level={2}>{preview?.title ?? fallbackTitle}</Typography.Title>
            {preview && <Tag color="cyan">真实员工档案</Tag>}
            <Tag>页面加载只读本地档案</Tag>
          </Space>
          {preview && (
            <Typography.Text type="secondary">
              <span>{preview.documentName}</span>
              <span> · {preview.targetSheet}</span>
              <span> · {preview.sheetStrategy}</span>
            </Typography.Text>
          )}
        </div>
        <Space wrap>
          {preview && (
            <Button href={preview.documentUrl} target="_blank" rel="noreferrer">
              打开在线表
            </Button>
          )}
          <Button onClick={() => void load()} loading={loading} disabled={syncing}>
            刷新档案
          </Button>
        </Space>
      </div>

      <Space wrap className="sheet-mapping-toolbar">
        <label>
          档案提交起始日期
          <Input
            aria-label="档案提交起始日期"
            type="date"
            value={submittedFrom}
            disabled={syncing}
            onChange={(event) => changeSubmissionDate(event.target.value, submittedTo)}
          />
        </label>
        <label>
          档案提交结束日期
          <Input
            aria-label="档案提交结束日期"
            type="date"
            value={submittedTo}
            disabled={syncing}
            onChange={(event) => changeSubmissionDate(submittedFrom, event.target.value)}
          />
        </label>
        <Button disabled={syncing} onClick={() => changeSubmissionDate('', '')}>
          清空提交日期
        </Button>
      </Space>
      {dateError && <Alert type="warning" message="提交起始日期不能晚于结束日期" />}
      {!loading && !targetItems.length && (submittedFrom || submittedTo) && (
        <Alert type="info" message="当前提交日期范围没有员工档案" />
      )}
      {targetItems.length > 0 && (
        <Select
          showSearch
          optionFilterProp="label"
          className="sheet-employee-select"
          value={preview?.rowKey}
          disabled={syncing || loading}
          onChange={(rowKey) => {
            if (syncing) return;
            setSelectedRowKey(rowKey);
            setReviewed(false);
            setConfirmOpen(false);
            setSyncError('');
          }}
          options={targetItems.map((item) => ({ value: item.rowKey, label: item.employeeName }))}
          aria-label="选择员工档案"
        />
      )}

      <div className="sheet-mapping-summary">
        <Summary label="目标字段" value={preview?.fields.length ?? 0} />
        <Summary label="直接映射" value={totals.mapped} />
        <Summary label="规则计算" value={totals.derived} />
        <Summary label="公式下拉" value={totals.formula} />
        <Summary label="确认留空" value={totals.blank} />
        <Summary label="待确认" value={totals.pending} tone={totals.pending > 0 ? 'warning' : undefined} />
        <Summary label="外部 API" value={`${preview?.externalCallCount ?? 0} 次`} />
      </div>

      {preview && (
        <Alert
          className="sheet-mapping-plan"
          type="info"
          showIcon
          message="写入顺序已锁定"
          description={`先从上一数据行仅复制 ${preview.writePlan.formulaHeaders.length} 个公式列，再写入 ${preview.writePlan.valueHeaders.length} 个普通字段；公式列不会写入固定值。`}
        />
      )}

      {preview && (
        <section className="sheet-sync-panel" aria-label={`${targetLabel}同步确认`}>
          <div className="sheet-sync-panel-heading">
            <div>
              <Space align="center" wrap>
                <Typography.Title level={4}>同步确认</Typography.Title>
                {preview.syncResult ? (
                  <Tag color="green">已完成</Tag>
                ) : preview.syncReadiness.ready ? (
                  <Tag color="blue">可以同步</Tag>
                ) : (
                  <Tag color="gold">等待补全</Tag>
                )}
              </Space>
              <Typography.Text type="secondary">两张表分别核对、分别执行，不会自动同步。</Typography.Text>
            </div>
            <Tag>确认后预计 {preview.syncReadiness.estimatedExternalCallCount} 次钉钉 API</Tag>
          </div>

          <div className="sheet-sync-scope">
            <SyncScope label="员工" value={employeeName || '-'} />
            <SyncScope label="档案提交" value={formatDateTime(preview.materialSubmittedAt)} />
            <SyncScope label="目标 Sheet" value={preview.targetSheet} />
            <SyncScope label="普通字段" value={`${preview.writePlan.valueHeaders.length} 个`} />
            <SyncScope label="公式列" value={`${preview.writePlan.formulaHeaders.length} 个`} />
            <SyncScope label="预计外部 API" value={`${preview.syncReadiness.estimatedExternalCallCount} 次`} />
          </div>

          {preview.syncResult ? (
            <Alert
              type="success"
              showIcon
              message="钉钉表格同步成功"
              description={`这是本地保存的上次结果，仍可重新校验当前在线表。执行人：${preview.syncResult.operator} · 完成时间：${formatDateTime(preview.syncResult.completedAt)} · 外部 API：${preview.syncResult.externalCallCount} 次`}
            />
          ) : preview.syncReadiness.ready ? (
            <Alert type="info" showIcon message="已具备同步条件" description="核对后只写入当前员工和当前目标表。" />
          ) : (
            <Alert
              type="warning"
              showIcon
              message={`还有 ${preview.syncReadiness.blockerCount} 个待确认字段，暂不能同步`}
              description="请先在上方字段列表中筛选“待确认”并补齐业务规则。"
            />
          )}

          {syncError && <Alert type="error" showIcon message="同步失败" description={syncError} />}

          <div className="sheet-sync-actions">
            <Checkbox
              checked={reviewed}
              disabled={!preview.syncReadiness.ready || syncing}
              onChange={(event) => setReviewed(event.target.checked)}
            >
              我已核对本次写入内容
            </Checkbox>
            <Button
              type="primary"
              disabled={!reviewed || !preview.syncReadiness.ready}
              loading={syncing}
              onClick={() => setConfirmOpen(true)}
            >
              {preview.syncResult ? '重新校验同步' : `确认同步到${targetLabel}`}
            </Button>
          </div>
        </section>
      )}

      {error && (
        <Alert type="error" showIcon message={error} action={<Button onClick={() => void load()}>重新加载</Button>} />
      )}

      <div className="sheet-mapping-toolbar">{preview && <Typography.Text code>{preview.rowKey}</Typography.Text>}</div>

      <FilteredTable<FieldPreview>
        config={adminConfig('mapping-fields', `${target}-${preview?.rowKey ?? ''}`)}
        project={(row, index) => projectAdmin('mapping-fields', row, index)}
        className="data-table sheet-mapping-table"
        rowKey={(row) => `${row.targetHeader}:${row.sourcePath}`}
        loading={loading}
        dataSource={fields}
        pagination={false}
        tableLayout="fixed"
        scroll={{ x: 1180 }}
        locale={{ emptyText: error ? '预演加载失败' : '没有符合条件的字段' }}
        columns={[
          { title: '在线表字段', dataIndex: 'targetHeader', width: 180 },
          { title: '系统来源', dataIndex: 'sourceLabel', width: 220 },
          {
            title: '来源路径',
            dataIndex: 'sourcePath',
            width: 260,
            render: (value) => value || '-'
          },
          { title: '转换规则', dataIndex: 'transform', width: 210 },
          {
            title: '预演结果',
            dataIndex: 'previewValue',
            width: 210,
            render: (value, field) => (
              <Space size={6} wrap>
                <span>{value || '-'}</span>
                {field.sensitive && <Tag>已脱敏</Tag>}
              </Space>
            )
          },
          {
            title: '状态',
            dataIndex: 'status',
            width: 120,
            fixed: 'right',
            render: (value) => <FieldStatusTag status={value} />
          }
        ]}
      />

      <Modal
        title={`最后确认：同步到${targetLabel}`}
        open={confirmOpen}
        okText="确认同步"
        cancelText="返回核对"
        confirmLoading={syncing}
        onOk={() => void confirmSync()}
        onCancel={() => setConfirmOpen(false)}
      >
        {preview && (
          <div className="sheet-sync-dialog">
            <Alert type="warning" showIcon message="确认后只执行当前员工的当前目标表；页面预览不会调用钉钉 API。" />
            <dl>
              <div>
                <dt>员工</dt>
                <dd>{employeeName || '-'}</dd>
              </div>
              <div>
                <dt>目标文档</dt>
                <dd>{preview.documentName}</dd>
              </div>
              <div>
                <dt>目标 Sheet</dt>
                <dd>{preview.targetSheet}</dd>
              </div>
              <div>
                <dt>写入范围</dt>
                <dd>
                  {preview.writePlan.valueHeaders.length} 个普通字段，{preview.writePlan.formulaHeaders.length} 个公式列
                </dd>
              </div>
            </dl>
          </div>
        )}
      </Modal>
    </section>
  );
}

function fieldValue(preview: MappingPreview, targetHeader: string) {
  return preview.fields.find((field) => field.targetHeader === targetHeader)?.previewValue ?? '';
}

function formatDateTime(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('zh-CN', { hour12: false });
}

function summarize(fields: FieldPreview[]) {
  return {
    mapped: fields.filter((field) => field.status === 'MAPPED').length,
    derived: fields.filter((field) => field.status === 'DERIVED').length,
    formula: fields.filter((field) => field.status === 'FORMULA_FILL_DOWN').length,
    blank: fields.filter((field) => field.status === 'INTENTIONALLY_BLANK').length,
    pending: fields.filter((field) => field.status === 'PENDING_CONFIRMATION').length
  };
}

function Summary({ label, value, tone }: { label: string; value: string | number; tone?: 'warning' }) {
  return (
    <div className={`sheet-mapping-metric ${tone ? `is-${tone}` : ''}`}>
      <Typography.Text type="secondary">{label}</Typography.Text>
      <strong>{value}</strong>
    </div>
  );
}

function SyncScope({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <Typography.Text type="secondary">{label}</Typography.Text>
      <strong>{value}</strong>
    </div>
  );
}

function FieldStatusTag({ status }: { status: FieldStatus }) {
  if (status === 'MAPPED') return <Tag color="green">已映射</Tag>;
  if (status === 'DERIVED') return <Tag color="blue">规则计算</Tag>;
  if (status === 'FORMULA_FILL_DOWN') return <Tag color="cyan">公式下拉</Tag>;
  if (status === 'INTENTIONALLY_BLANK') return <Tag>确认留空</Tag>;
  return <Tag color="gold">待确认</Tag>;
}
